import { format, parseISO } from 'date-fns';
import {
    EquipmentTypeValues, UsaStateCodes, UsaTerritoryCodes, CanadianProvinceCodes, NetworkInsightsReportType
} from './enums';

export const buildAddressObject = (prefix: string, data: any): object => {
    // build the address object to be used by the search actions
    return {
        [`${prefix}Street`]: data.street,
        [`${prefix}City`]: data.city,
        [`${prefix}State`]: data.state,
        [`${prefix}Postal`]: data.zip,
        [`${prefix}Country`]: data.country === 'US' ? 'USA' : data.country
    };
};

const buildAreaLabel = (areaDisplay: string): string => {
    const [state, city] = areaDisplay.split(' - ');

    return `${city}, ${state}`;
};

export const buildLaneOriginObject = (lane: any): object => {
    return {
        label: lane.origin_area_display ? buildAreaLabel(lane.origin_area_display) : null,
        city: lane.origin_city,
        state: lane.origin_state,
        zip: lane.origin_postal ? lane.origin_postal : null
    };
};

export const buildLaneDestinationObject = (lane: any): object => {
    return {
        label: lane.destination_area_display ? buildAreaLabel(lane.destination_area_display) : null,
        city: lane.destination_city,
        state: lane.destination_state,
        zip: lane.destination_postal ? lane.destination_postal : null
    };
};

export const formatCurrency = (amount: number | null, decimal: number): string => {
    let formattedCurrency = 'N/A';

    if (amount !== undefined && amount !== null) {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: decimal,
            maximumFractionDigits: decimal
        });

        formattedCurrency = formatter.format(amount);
    }

    return formattedCurrency;
};

export const formatRevenueRowCurrency = (amount: number | null, decimal: number): string => {
    let formattedCurrency = '$0.00';

    if (amount !== undefined && amount !== null) {
        const formatter = new Intl.NumberFormat('en-Us', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: decimal,
            maximumFractionDigits: decimal
        });

        formattedCurrency = formatter.format(amount);
    }

    return formattedCurrency;
};

export const formatNumber = (inputNumber: number | null, maximumDecimals: number, minimumDecimals: number = 0): string => {
    let formattedNumber = 'N/A';

    if (inputNumber !== undefined && inputNumber !== null) {
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: minimumDecimals,
            maximumFractionDigits: maximumDecimals
        });

        formattedNumber = formatter.format(inputNumber);
    }
    return formattedNumber;
};

export const formatNumberPercentage = (inputNumber: number | null, maximumDecimals: number, minimumDecimals: number = 0): string => {
    let formattedNumberPercentage = 'N/A';

    if (inputNumber !== undefined && inputNumber !== null) {
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: minimumDecimals,
            maximumFractionDigits: maximumDecimals
        });

        formattedNumberPercentage = formatter.format(inputNumber);
    }
    return `${formattedNumberPercentage}%`;
};

export const formatPositiveNumberNoRounding = (inputNumber: number | null, decimals: number = 0): string => {
    let formattedNumber = 'N/A';

    if (inputNumber !== undefined && inputNumber !== null && inputNumber >= 0) {
        const numWithRounding = parseFloat(inputNumber.toFixed(decimals));
        formattedNumber = numWithRounding <= inputNumber ? numWithRounding.toString() : (numWithRounding - (0.1 ** decimals)).toFixed(decimals);
    }

    return formattedNumber;
};

export const getShortMonthFromDate = (inputDate: any): string => {
    const result = format(parseISO(inputDate), 'MMM');

    return result;
};

export const getMonthFromDate = (inputDate: any): string => {
    const result = format(parseISO(inputDate), 'MMMM');

    return result;
};

export const getYearFromDate = (inputDate: any): string => {
    const result = format(parseISO(inputDate), 'yyyy');

    return result;
};

export const getPercentageDifference = (previousAmount: any, currentAmount: any, decimal: number): string | number | null => {
    const percentageDifferenceVolume = previousAmount && currentAmount ?
        (((currentAmount - previousAmount) / previousAmount) * 100) :
        null;

    return percentageDifferenceVolume ? formatNumber(percentageDifferenceVolume, decimal) : percentageDifferenceVolume;
};

export const groupBy = (objectArray: any, property: any): any => {
    return objectArray.reduce((acc: any, obj: any) => {
        const key = obj[property];
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
    }, {});
};

export const equipmentTypeOptions = [
    { value: EquipmentTypeValues.DryVan, label: 'Dry Van' },
    { value: EquipmentTypeValues.FlatBed, label: 'Flatbed' },
    { value: EquipmentTypeValues.Refrigerated, label: 'Refrigerated' }
];

export const usaStateOptions = [
    { value: UsaStateCodes.AL, label: 'Alabama' },
    { value: CanadianProvinceCodes.AB, label: 'Alberta' },
    { value: UsaStateCodes.AK, label: 'Alaska' },
    { value: UsaStateCodes.AZ, label: 'Arizona' },
    { value: UsaStateCodes.AR, label: 'Arkansas' },
    { value: CanadianProvinceCodes.BC, label: 'British Columbia' },
    { value: UsaStateCodes.CA, label: 'California' },
    { value: UsaStateCodes.CO, label: 'Colorado' },
    { value: UsaStateCodes.CT, label: 'Connecticut' },
    { value: UsaStateCodes.DE, label: 'Delaware' },
    { value: UsaStateCodes.DC, label: 'District of Columbia' },
    { value: UsaStateCodes.FL, label: 'Florida' },
    { value: UsaStateCodes.GA, label: 'Georgia' },
    { value: UsaTerritoryCodes.GU, label: 'Guam' },
    { value: UsaStateCodes.HI, label: 'Hawaii' },
    { value: UsaStateCodes.ID, label: 'Idaho' },
    { value: UsaStateCodes.IL, label: 'Illinois' },
    { value: UsaStateCodes.IN, label: 'Indiana' },
    { value: UsaStateCodes.IA, label: 'Iowa' },
    { value: UsaStateCodes.KS, label: 'Kansas' },
    { value: UsaStateCodes.KY, label: 'Kentucky' },
    { value: UsaStateCodes.LA, label: 'Louisiana' },
    { value: UsaStateCodes.ME, label: 'Maine' },
    { value: CanadianProvinceCodes.MB, label: 'Manitoba' },
    { value: UsaStateCodes.MD, label: 'Maryland' },
    { value: UsaStateCodes.MA, label: 'Massachusetts' },
    { value: UsaStateCodes.MI, label: 'Michigan' },
    { value: UsaStateCodes.MN, label: 'Minnesota' },
    { value: UsaStateCodes.MS, label: 'Mississippi' },
    { value: UsaStateCodes.MO, label: 'Missouri' },
    { value: UsaStateCodes.MT, label: 'Montana' },
    { value: UsaStateCodes.NE, label: 'Nebraska' },
    { value: UsaStateCodes.NV, label: 'Nevada' },
    { value: CanadianProvinceCodes.NB, label: 'New Brunswick' },
    { value: UsaStateCodes.NH, label: 'New Hampshire' },
    { value: UsaStateCodes.NJ, label: 'New Jersey' },
    { value: UsaStateCodes.NM, label: 'New Mexico' },
    { value: UsaStateCodes.NY, label: 'New York' },
    { value: CanadianProvinceCodes.NL, label: 'Newfoundland and Labrador' },
    { value: UsaStateCodes.NC, label: 'North Carolina' },
    { value: UsaStateCodes.ND, label: 'North Dakota' },
    { value: CanadianProvinceCodes.NT, label: 'Northwest Territories' },
    { value: CanadianProvinceCodes.NS, label: 'Nova Scotia' },
    { value: CanadianProvinceCodes.NU, label: 'Nunavut' },
    { value: UsaStateCodes.OH, label: 'Ohio' },
    { value: UsaStateCodes.OK, label: 'Oklahoma' },
    { value: CanadianProvinceCodes.ON, label: 'Ontario' },
    { value: UsaStateCodes.OR, label: 'Oregon' },
    { value: UsaStateCodes.PA, label: 'Pennsylvania' },
    { value: CanadianProvinceCodes.PE, label: 'Prince Edward Island' },
    { value: CanadianProvinceCodes.QC, label: 'Quebec' },
    { value: UsaStateCodes.RI, label: 'Rhode Island' },
    { value: CanadianProvinceCodes.SK, label: 'Saskatchewan' },
    { value: UsaStateCodes.SC, label: 'South Carolina' },
    { value: UsaStateCodes.SD, label: 'South Dakota' },
    { value: UsaStateCodes.TN, label: 'Tennessee' },
    { value: UsaStateCodes.TX, label: 'Texas' },
    { value: UsaStateCodes.UT, label: 'Utah' },
    { value: UsaStateCodes.VT, label: 'Vermont' },
    { value: UsaStateCodes.VA, label: 'Virginia' },
    { value: UsaStateCodes.WA, label: 'Washington' },
    { value: UsaStateCodes.WV, label: 'West Virginia' },
    { value: UsaStateCodes.WI, label: 'Wisconsin' },
    { value: UsaStateCodes.WY, label: 'Wyoming' },
    { value: CanadianProvinceCodes.YT, label: 'Yukon' }
];

export const getGeographyTableHeaders = (selectedReportType: string, isTableExpanded: boolean): any => {
    const tableHeaders: any = [
        {
            displayText: 'Lane', dataId: 'lane_name'
        }
    ];

    const selectedTableHeaderList = {
        Volume: {
            displayText: NetworkInsightsReportType.Volume, dataId: 'order_volume', currency: false, decimals: 2
        },
        AveragePrice: {
            displayText: NetworkInsightsReportType.AveragePrice, dataId: 'price_per_mile', currency: true, decimals: 2
        },
        HaulLength: {
            displayText: NetworkInsightsReportType.HaulLength, dataId: 'loh', currency: false, decimals: 2
        },
        EmptyMiles: {
            displayText: NetworkInsightsReportType.EmptyMiles, dataId: 'empty_miles', currency: false, decimals: 2
        },
        EmptyMilesPercent: {
            displayText: `${NetworkInsightsReportType.EmptyMiles} %`, dataId: 'empty_miles_percent', currency: false, decimals: 0
        },
        MilesPerHour: {
            displayText: NetworkInsightsReportType.MilesPerHour, dataId: 'miles_per_hour', currency: false, decimals: 0
        },
        RevenuePerHour: {
            displayText: NetworkInsightsReportType.RevenuePerHour, dataId: 'revenue_per_hour', currency: true, decimals: 2
        },
        DwellTime: {
            displayText: NetworkInsightsReportType.DwellTime, dataId: 'dwell_time', currency: false, decimals: 2
        },
        InTransitDelay: {
            displayText: NetworkInsightsReportType.InTransitDelay, dataId: 'in_transit_delay', currency: false, decimals: 2
        }
    };

    if (isTableExpanded === false) {
        const selectedReportTypeHeaderName = Object.keys(selectedTableHeaderList).find((tableHeaderName) => {
            return tableHeaderName.toLowerCase() === selectedReportType.toLowerCase();
        });

        if (selectedReportTypeHeaderName !== undefined) {
            tableHeaders.push((selectedTableHeaderList as any)[selectedReportTypeHeaderName]);

            // add on additional display column for the empty miles percent
            if (selectedReportTypeHeaderName.toLowerCase() === 'emptymiles') {
                tableHeaders.push(selectedTableHeaderList.EmptyMilesPercent);
            }
        }
    } else {
        tableHeaders.push(
            selectedTableHeaderList.Volume,
            selectedTableHeaderList.AveragePrice,
            selectedTableHeaderList.HaulLength,
            selectedTableHeaderList.EmptyMiles,
            selectedTableHeaderList.EmptyMilesPercent,
            selectedTableHeaderList.MilesPerHour,
            selectedTableHeaderList.RevenuePerHour,
            selectedTableHeaderList.DwellTime,
            selectedTableHeaderList.InTransitDelay,
            {
                displayText: 'Shippers', dataId: 'shipper_count', currency: false, decimals: 0
            },
            {
                displayText: 'Origin Area', dataId: 'origin_area'
            },
            {
                displayText: 'Origin State', dataId: 'origin_state'
            },
            {
                displayText: 'Destination Area', dataId: 'destination_area'
            },
            {
                displayText: 'Destination State', dataId: 'destination_state'
            }
        );
    }

    return tableHeaders;
};

export const renderRateResultTypeValue = (type: string): string => {
    switch (type) {
        case 'AREA2AREA':
            return 'Area Rates';
        case 'STATE2STATE':
            return 'State Rates';
        case 'REGION2REGION':
            return 'Regional Rates';
        case 'MARKET2MARKET':
            return 'Market Rates';
        case 'NATIONAL2NATIONAL':
            return 'National Rates';
        default:
            return 'National Rates';
    }
};

export const renderSubscriptionTypeValue = (type: string): string => {
    switch (type) {
        case 'TLR_MP':
            return 'Monthly';
        case 'TLR_AP':
            return 'Yearly';
        default:
            return 'Invalid Plan';
    }
};

const stateAbbreviationToFullName: { [key: string]: string } = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming'
};

export const formatStateCity = (input: string): string => {
    const parts: string[] = input.split(/\s*-\s*/);

    if (parts.length !== 2) {
        return 'Invalid input format';
    }
    const [state, city] = parts;

    const formattedState: string = state.trim().toUpperCase();
    const formattedCity: string = city
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c: string) => { return c.toUpperCase(); });

    return `${formattedState} - ${formattedCity}`;
};
// Arrow function to convert state abbreviation to full name
export const getFullStateName = (abbreviation: string): string | undefined => { return stateAbbreviationToFullName[abbreviation]; };
